import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { api } from "@/lib/api";

const WEEKDAYS = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];

const pad = (n) => String(n).padStart(2, "0");

export default function Calendar({ venueId, selectedDate, onSelect, endpoint }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const year = cursor.getFullYear();
  const month = cursor.getMonth() + 1;
  const url = endpoint || `/public/venues/${venueId}/availability`;

  const { data } = useQuery({
    queryKey: ["availability", venueId, year, month, endpoint],
    queryFn: async () => (await api.get(url, { params: { year, month } })).data,
    enabled: !!venueId,
    refetchInterval: 8000,
  });
  const booked = new Set(data?.booked || []);

  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const prev = () => setCursor(new Date(year, month - 2, 1));
  const next = () => setCursor(new Date(year, month, 1));

  return (
    <div className="card-lux p-4 sm:p-6" data-testid="calendar">
      <div className="flex items-center justify-between mb-5">
        <button onClick={next} data-testid="cal-next" className="w-11 h-11 rounded-full glass flex items-center justify-center text-gold hover:bg-navy3 transition-colors" aria-label="الشهر التالي">
          <ChevronLeft size={22} />
        </button>
        <div className="font-display text-xl sm:text-2xl text-offwhite">{MONTHS[month - 1]} {year}</div>
        <button onClick={prev} data-testid="cal-prev" className="w-11 h-11 rounded-full glass flex items-center justify-center text-gold hover:bg-navy3 transition-colors" aria-label="الشهر السابق">
          <ChevronRight size={22} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[11px] sm:text-xs text-muted2 py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const ds = `${year}-${pad(month)}-${pad(d)}`;
          const isBooked = booked.has(ds);
          const isPast = ds < todayStr;
          const isSelected = ds === selectedDate;
          const disabled = isBooked || isPast;
          return (
            <button
              key={i}
              data-testid={`day-${ds}`}
              disabled={disabled}
              onClick={() => !disabled && onSelect && onSelect(ds)}
              className={[
                "aspect-square rounded-xl text-sm font-medium flex flex-col items-center justify-center transition-colors relative",
                isSelected ? "bg-gold text-navy font-bold" : "",
                !isSelected && isBooked ? "bg-red-950/40 text-red-400/70 cursor-not-allowed line-through" : "",
                !isSelected && isPast ? "text-muted2/30 cursor-not-allowed" : "",
                !isSelected && !disabled ? "bg-navy3/60 text-offwhite hover:bg-gold/20 hover:text-gold" : "",
              ].join(" ")}
            >
              {d}
              {!isSelected && !disabled && <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-0.5" />}
              {!isSelected && isBooked && <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-0.5" />}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-6 mt-5 text-xs text-muted2">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> متاح</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> محجوز</span>
      </div>
    </div>
  );
}
